import React, { useState } from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useParamsInt } from '../../../../../shared/hooks/location'
import { Form, FormText, FormCheckbox, FormSubmitBack, FormNumber } from '../../../../../shared/form/form'
import { Card } from '../../../../../shared/card/card'
import { Menu, MenuItem } from '../../../../../shared/menu/menu'
import { IconEdit, IconShield } from '../../../../../shared/icon/icon'
import { ScrollY } from '../../../../../shared/component/scroll'
import { useRoleSysOrganization, useRoleSysProvider } from '../../../../../role/hook/role-sys'
import { OrganizationLogo } from './organization-edit/organization-logo'
import { ApiAccessToggle } from './organization-edit/api-access-toggle'
import { AIToolsSharingCard } from './organization-edit/ai-tools-sharing-card'
import { useApiFormOrganization } from '../../../../../api-new/state/content'
import { useApiFormOrgEntitlement, useApiListOrgEntitlement } from '../../../../../api-new/state/auth'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { useAuthContext } from '../../../../../context/auth.context'
import { lmsAdminVisible } from '../../../../../view/lms-admin/lms-admin-gate'

export function OrganizationEdit() {
  const { organizationID } = useParamsInt()

  const roleSysOrganization = useRoleSysOrganization()
  const roleSysProvider = useRoleSysProvider()

  // The LMS switch (organizations.feature_lms) is a feature like AI, saved
  // by the same form. While the LMS is staff-only (staff-only.js) only a
  // system administrator or root sees it; anyone else's save still sends
  // back the value the form loaded.
  const authContext = useAuthContext()
  const showFeatureLMS = lmsAdminVisible(authContext.state)

  const apiFormOrganization = useApiFormOrganization()
  const apiListOrgEntitlement = useApiListOrgEntitlement()
  const apiFormOrgEntitlement = useApiFormOrgEntitlement()

  const [featureSSO, setFeatureSSO] = useState(false)
  const [entitlementError, entitlementFailure, setEntitlementError] = useErrorLine()

  const handlePath = async () => {
    await apiFormOrganization.get(organizationID)

    try {
      const entitlement = await apiListOrgEntitlement.get(organizationID)
      setFeatureSSO(!!(entitlement && entitlement.featureSSO))
    } catch (err) {
      // Expected until the backend adds GET /org-entitlement/:organizationID
      // (see api-new/state/auth.js) -- degrade to unchecked.
      setFeatureSSO(false)
    }
  }

  const handleSubmit = async data => await apiFormOrganization.put(data.id, data)

  // Saves through the entitlement endpoint directly, on change -- NOT
  // through the content organization form's onSubmit above. The content
  // service's own organization aggregate doesn't carry featureSSO at all
  // (see organization-sso.js's header comment), so this checkbox lives
  // outside the <Form> below and never becomes part of that submit payload.
  const handleToggleFeatureSSO = async e => {
    const next = e.target.checked
    setEntitlementError(null)

    try {
      await apiFormOrgEntitlement.put(organizationID, { featureSSO: next })
      setFeatureSSO(next)
    } catch (err) {
      setEntitlementError((err && err.data && err.data.message) || 'Could not update the SSO entitlement.', err)
    }
  }

  const HeaderMenu = () =>
    <Menu>
      <MenuItem Icon={IconShield} text="sso" to={`/admin/organization/list/edit/${organizationID}/sso`} show={roleSysProvider.accessRead} />
    </Menu>

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconEdit} text={apiFormOrganization.form.name} onPath={handlePath}>
        <ScrollY>
          <Card title="Organization Edit" HeaderMenu={HeaderMenu}>
            <Form
              state={apiFormOrganization.form}
              submitting={apiFormOrganization.submitting}
              onSubmit={handleSubmit}
              disabled={!roleSysOrganization.accessUpdate}>
              <FormText label="Name" name="name" placeholder="organization name" required />
              <FormNumber label="user limit" name="limitUser" />
              <FormNumber label="case limit" name="limitCase" />
              <FormCheckbox label="feature analytics" name="featureAnalytic" />
              <FormCheckbox label="feature multiplayer" name="featureMultiplayer" />
              <FormCheckbox label="feature assessments" name="featureAssessment" />
              <FormCheckbox label="feature heatmap" name="featureHeatmap" />
              <FormCheckbox label="feature AR" name="featureAR" />
              <FormCheckbox label="feature tradeshow" name="featureTradeShow" />
              <FormCheckbox label="feature AI" name="featureAI" />
              {showFeatureLMS &&
                <React.Fragment>
                  <FormCheckbox label="feature LMS connector" name="featureLMS" />
                  <div className="form-text mt-0 mb-2">Turns on LMS launches and LMS account linking for this organization.</div>
                </React.Fragment>}
              <FormCheckbox label="access view public content" name="accessViewPublic" />
              <FormSubmitBack text="update organization" />
            </Form>

            <div className="form-check form-switch mt-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="organization-edit-feature-sso"
                checked={featureSSO}
                disabled={!roleSysProvider.accessUpdate}
                onChange={handleToggleFeatureSSO}
              />
              <label className="form-check-label" htmlFor="organization-edit-feature-sso">
                feature SSO
              </label>
            </div>

            {entitlementError &&
              <div className="text-danger small mt-1">
                {entitlementError}
              </div>}
            {entitlementError && <ErrorReference error={entitlementFailure} />}
          </Card>

          <ApiAccessToggle organizationID={organizationID} />

          <AIToolsSharingCard organizationID={organizationID} />

          <OrganizationLogo organizationID={organizationID} disabled={!roleSysOrganization.accessUpdate} />
        </ScrollY>
      </BreadcrumbView>
    </React.Fragment>
  )
}
